import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpChecklistApprovePermission } from '@internal/ksdp-permissions-common';
import { Checklist, ChecklistRequirement, PendingChecklist } from '../types';
import { ChecklistApprovalDialog } from './ChecklistApprovalDialog';
import { UnsavedChangesGuard } from './UnsavedChangesGuard';

// Regeneration functionality is commented out for future reference

const MAX_QUESTION_LENGTH = 500;

interface EditorLocationState {
  checklist?: PendingChecklist;
}

interface NormalizationResult {
  checklist: Checklist;
  emptyRequirementIds: Set<string | number>;
}

type RequirementReviewStatus = 'pending' | 'accepted' | 'approved' | 'edit';

interface RequirementReview {
  status: RequirementReviewStatus;
  //  comment: string;
}

interface EditorSnapshot {
  checklist: Checklist;
  reviews: Record<string, RequirementReview>;
}

function createEmptyReview(): RequirementReview {
  return {
    status: 'pending',
    //    comment: '',
  };
}

function cloneChecklist(checklist: Checklist): Checklist {
  return JSON.parse(JSON.stringify(checklist)) as Checklist;
}

function cloneReviews(
  reviews: Record<string, RequirementReview>,
): Record<string, RequirementReview> {
  return JSON.parse(JSON.stringify(reviews)) as Record<
    string,
    RequirementReview
  >;
}

function createInitialReviews(
  checklist: Checklist,
): Record<string, RequirementReview> {
  return checklist.requirements.reduce<Record<string, RequirementReview>>(
    (result, requirement) => {
      result[requirement.requirement_id] = {
        status:
          requirement.review.status === 'APPROVED' ? 'approved' : 'pending',
        //    comment: '',
      };
      return result;
    },
    {},
  );
}

function stableEditorValue(snapshot: EditorSnapshot | null): string {
  if (!snapshot) {
    return '';
  }

  return JSON.stringify(snapshot);
}

function normalizeRequirement(requirement: ChecklistRequirement): {
  requirement: ChecklistRequirement;
  empty: boolean;
} {
  const seen = new Set<string>();

  const normalizedQuestions = requirement.checklist
    .map(item => item.question.trim())
    .filter(question => question.length > 0)
    .filter(question => {
      const duplicateKey = question.toLocaleLowerCase();

      if (seen.has(duplicateKey)) {
        return false;
      }

      seen.add(duplicateKey);
      return true;
    })
    .map(question => ({ question }));

  return {
    requirement: {
      ...requirement,
      checklist: normalizedQuestions,
    },
    empty: normalizedQuestions.length === 0,
  };
}

function normalizeChecklist(checklist: Checklist): NormalizationResult {
  const emptyRequirementIds = new Set<string | number>();

  const requirements = checklist.requirements.map(requirement => {
    const normalized = normalizeRequirement(requirement);

    if (normalized.empty) {
      emptyRequirementIds.add(requirement.requirement_id);
    }

    return normalized.requirement;
  });

  return {
    checklist: {
      ...checklist,
      requirements,
    },
    emptyRequirementIds,
  };
}

function formatDescription(description?: string): string {
  if (!description) {
    return '';
  }

  return (
    description
      .replace(/\r\n?/g, '\n')
      .replace(/[ \t]*\n[ \t]*/g, '\n')
      // preserve paragraph breaks
      .replace(/\n{3,}/g, '\n\n')
      // turn single line breaks into spaces
      .replace(/(?<!\n)\n(?!\n)/g, ' ')
      .trim()
  );
}

function formatDate(value: string): string {
  if (!value) return 'Unknown';

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
}

function questionKey(
  requirementId: string | number,
  questionIndex: number,
): string {
  return `${requirementId}-${questionIndex}`;
}

function isRequirementLocked(requirement: ChecklistRequirement): boolean {
  return requirement.review.status === 'APPROVED';
}

function createInitialCollapsedRequirements(
  checklist: Checklist,
): Set<string | number> {
  return new Set(
    checklist.requirements
      .filter(isRequirementLocked)
      .map(requirement => requirement.requirement_id),
  );
}

export function ChecklistEditorPage() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  // UI-only gate: viewers see the checklist read-only. Save and approve are
  // enforced by the backend router (ksdp.checklist.approve).
  const { allowed: canApprove } = usePermission({
    permission: ksdpChecklistApprovePermission,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { checklistId } = useParams<{
    checklistId: string;
  }>();

  const listPath = '/checklist-approval';

  const locationState = location.state as EditorLocationState | null;

  const [metadata, setMetadata] = useState<PendingChecklist | null>(
    locationState?.checklist ?? null,
  );

  const [draft, setDraft] = useState<Checklist | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<EditorSnapshot | null>(
    null,
  );
  const [approvalChecklist, setApprovalChecklist] = useState<Checklist | null>(
    null,
  );
  const [reviews, setReviews] = useState<Record<string, RequirementReview>>({});
  const [editingQuestions, setEditingQuestions] = useState<Set<string>>(
    new Set(),
  );

  const [collapsedRequirementIds, setCollapsedRequirementIds] = useState<
    Set<string | number>
  >(new Set());

  const toggleRequirementCollapsed = (requirementId: string | number) => {
    setCollapsedRequirementIds(previous => {
      const next = new Set(previous);

      if (next.has(requirementId)) {
        next.delete(requirementId);
      } else {
        next.add(requirementId);
      }

      return next;
    });
  };

  const [emptyRequirementIds, setEmptyRequirementIds] = useState<
    Set<string | number>
  >(new Set());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [navigationAllowed, setNavigationAllowed] = useState(false);

  const currentSnapshot = useMemo<EditorSnapshot | null>(() => {
    if (!draft) {
      return null;
    }

    return {
      checklist: draft,
      reviews,
    };
  }, [draft, reviews]);

  const dirty =
    !navigationAllowed &&
    currentSnapshot !== null &&
    savedSnapshot !== null &&
    stableEditorValue(currentSnapshot) !== stableEditorValue(savedSnapshot);

  const acceptedRequirements = useMemo(
    () =>
      Object.values(reviews).filter(
        review => review.status === 'accepted' || review.status === 'approved',
      ).length,
    [reviews],
  );

  const editRequirements = useMemo(
    () =>
      Object.values(reviews).filter(review => review.status === 'edit').length,
    [reviews],
  );

  const pendingRequirements = useMemo(
    () =>
      draft?.requirements.filter(
        requirement =>
          (reviews[requirement.requirement_id]?.status ?? 'pending') ===
          'pending',
      ).length ?? 0,
    [draft, reviews],
  );

  const hasOverlongQuestion = useMemo(
    () =>
      draft?.requirements.some(requirement =>
        requirement.checklist.some(
          item => item.question.length > MAX_QUESTION_LENGTH,
        ),
      ) ?? false,
    [draft],
  );

  const hasEditRequests = editRequirements > 0;

  const loadMetadata = useCallback(async () => {
    if (metadata || !checklistId) {
      return;
    }

    try {
      const baseUrl = await discoveryApi.getBaseUrl('checklist-approval');

      const response = await fetchApi.fetch(`${baseUrl}/checklist-validation/`);

      if (!response.ok) {
        throw new Error();
      }

      const payload = await response.json();
      const checklists = payload.checklists as PendingChecklist[];

      const matchingChecklist = checklists.find(
        checklist => checklist.checklist_id === checklistId,
      );

      if (!matchingChecklist) {
        throw new Error();
      }

      setMetadata(matchingChecklist);
    } catch {
      setLoadError('Could not load checklist details.');
      setLoading(false);
    }
  }, [metadata, checklistId, discoveryApi, fetchApi]);

  const loadChecklist = useCallback(async () => {
    if (!checklistId) {
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('checklist-approval');

      const response = await fetchApi.fetch(
        `${baseUrl}/checklist-validation/${encodeURIComponent(checklistId)}`,
      );

      if (!response.ok) {
        throw new Error();
      }

      const checklist = (await response.json()) as Checklist;
      const initialDraft = cloneChecklist(checklist);
      const initialReviews = createInitialReviews(initialDraft);

      setDraft(initialDraft);
      setReviews(initialReviews);
      setCollapsedRequirementIds(
        createInitialCollapsedRequirements(initialDraft),
      );

      setSavedSnapshot({
        checklist: cloneChecklist(initialDraft),
        reviews: cloneReviews(initialReviews),
      });
    } catch {
      setLoadError('Could not load the checklist.');
    } finally {
      setLoading(false);
    }
  }, [checklistId, discoveryApi, fetchApi]);

  useEffect(() => {
    void loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    void loadChecklist();
  }, [loadChecklist]);

  const updateQuestion = (
    requirementIndex: number,
    questionIndex: number,
    value: string,
  ) => {
    const requirement = draft?.requirements[requirementIndex];

    if (!requirement || isRequirementLocked(requirement)) {
      return;
    }

    const requirementId = requirement.requirement_id;

    setDraft(previous => {
      if (!previous) {
        return previous;
      }

      const next = cloneChecklist(previous);
      const nextRequirement = next.requirements[requirementIndex];

      if (!nextRequirement?.checklist[questionIndex]) {
        return previous;
      }

      nextRequirement.checklist[questionIndex].question = value;
      return next;
    });

    setCollapsedRequirementIds(previous => {
      const next = new Set(previous);
      next.delete(requirementId);
      return next;
    });

    setActionError(null);

    if (value.trim().length > 0) {
      setEmptyRequirementIds(previous => {
        const next = new Set(previous);
        next.delete(requirementId);
        return next;
      });
    }
  };

  const updateRequirementStatus = (
    requirementId: number | string,
    status: RequirementReviewStatus,
  ) => {
    setReviews(previous => {
      const current = previous[requirementId] ?? createEmptyReview();

      return {
        ...previous,
        [requirementId]: {
          ...current,
          status,
        },
      };
    });

    setCollapsedRequirementIds(previous => {
      const next = new Set(previous);

      if (status === 'accepted') {
        next.add(requirementId);
      } else {
        next.delete(requirementId);
      }

      return next;
    });

    setActionError(null);
  };

  /* const updateRequirementComment = (
    requirementId: number | string,
    comment: string,
  ) => {
    setReviews(previous => {
      const current =
        previous[requirementId] ?? createEmptyReview();

      const next = { ...previous };

      next[requirementId] = {
        ...current,
        comment,
      };

      return next;
    });

    setActionError(null);
  };*/

  const prepareChecklist = (): Checklist | null => {
    if (!draft || draft.requirements.length === 0) {
      setActionError('The checklist must contain at least one requirement.');
      return null;
    }

    if (hasOverlongQuestion) {
      setActionError(
        `Questions cannot exceed ${MAX_QUESTION_LENGTH} characters.`,
      );
      return null;
    }

    const normalized = normalizeChecklist(draft);

    setEmptyRequirementIds(normalized.emptyRequirementIds);

    if (normalized.emptyRequirementIds.size > 0) {
      setActionError('Every requirement must contain at least one question.');
      return null;
    }

    return normalized.checklist;
  };

  const saveChecklist = async (): Promise<Checklist | null> => {
    const effectiveChecklistId = checklistId ?? metadata?.checklist_id;

    if (!effectiveChecklistId) {
      setActionError('The checklist ID is missing.');
      return null;
    }

    const normalizedChecklist = prepareChecklist();

    if (!normalizedChecklist) {
      return null;
    }
    setSaving(true);
    setActionError(null);

    try {
      const modifiedRequirements = normalizedChecklist.requirements
        .filter(requirement => {
          const savedRequirement = savedSnapshot?.checklist.requirements.find(
            r => r.requirement_id === requirement.requirement_id,
          );

          const checklistChanged =
            JSON.stringify(requirement) !== JSON.stringify(savedRequirement);

          const currentReview = reviews[requirement.requirement_id]?.status;

          const savedReview =
            savedSnapshot?.reviews[requirement.requirement_id]?.status;

          const reviewChanged =
            currentReview !== savedReview && currentReview === 'accepted';

          return checklistChanged || reviewChanged;
        })
        .map(requirement => ({
          requirement_hash: requirement.requirement_hash,
          checklist: requirement.checklist,
          review_status: reviews[requirement.requirement_id]?.status,
        }));

      const proxyUrl = await discoveryApi.getBaseUrl('checklist-approval');

      const response = await fetchApi.fetch(
        `${proxyUrl}/checklist-validation/${encodeURIComponent(
          effectiveChecklistId,
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            modified_requirements: modifiedRequirements,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Save request failed: ${response.status}`);
      }

      const persistedDraft = cloneChecklist(normalizedChecklist);

      setDraft(persistedDraft);
      setSavedSnapshot({
        checklist: cloneChecklist(persistedDraft),
        reviews: cloneReviews(reviews),
      });
      setSavedAt(new Date());
      setEmptyRequirementIds(new Set());
      setEditingQuestions(new Set());

      return persistedDraft;
    } catch {
      setActionError(
        'Could not save checklist changes. Your edits have been retained.',
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openApprovalDialog = async () => {
    if (!draft || approving || saving) {
      return;
    }

    if (pendingRequirements > 0) {
      setActionError(
        'Review every requirement before approving the checklist.',
      );
      return;
    }

    if (hasEditRequests) {
      setActionError(
        'The checklist cannot be approved unless all requirements are approved or accepted. Save the review instead.',
      );
      return;
    }

    let checklistToApprove = prepareChecklist();

    if (!checklistToApprove) {
      return;
    }

    if (dirty) {
      checklistToApprove = await saveChecklist();
    }

    if (!checklistToApprove) {
      return;
    }

    setApprovalChecklist(checklistToApprove);
    setApprovalDialogOpen(true);
  };

  const approveChecklist = async () => {
    const effectiveChecklistId = checklistId ?? metadata?.checklist_id;

    if (!effectiveChecklistId) {
      setActionError('The checklist ID is missing.');
      return;
    }

    setApproving(true);
    setActionError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('checklist-approval');
      const response = await fetchApi.fetch(
        `${baseUrl}/checklist-validation/${encodeURIComponent(
          effectiveChecklistId,
        )}/approval`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error(`Approval request failed: ${response.status}`);
      }

      setNavigationAllowed(true);
      setApprovalDialogOpen(false);
      setApprovalChecklist(null);

      navigate(listPath, {
        replace: true,
        state: {
          approvalMessage: `Checklist for ${
            metadata?.standard ?? 'the standard'
          } was approved.`,
        },
      });
    } catch {
      setActionError('Could not approve the checklist.');
    } finally {
      setApproving(false);
    }
  };

  const leavePage = () => {
    if (
      dirty &&
      !window.confirm(
        'You have unsaved changes. Leave this page and discard them?',
      )
    ) {
      return;
    }

    setNavigationAllowed(true);
    navigate(listPath);
  };

  if (loading) {
    return (
      <div className="card checklist-editor-summary">
        <p className="loading-text">Loading checklist…</p>
      </div>
    );
  }

  if (loadError || !draft || !savedSnapshot || !metadata) {
    return (
      <>
        <button
          type="button"
          className="back-button"
          onClick={() => navigate(listPath)}
        >
          <span aria-hidden="true">←</span>
          Back to pending checklists
        </button>

        <div className="checklist-error" role="alert">
          {loadError ?? 'Checklist information is missing.'}
        </div>
      </>
    );
  }

  return (
    <UnsavedChangesGuard active={dirty}>
      <button type="button" className="back-button" onClick={leavePage}>
        <span aria-hidden="true">←</span>
        Back to pending checklists
      </button>

      <div className="card checklist-editor-summary">
        <div className="checklist-editor-heading">
          <div>
            <h2>{metadata.standard}</h2>
            <p>
              {/* Accept each requirement, request regeneration, or manually edit individual questions. */}
              Accept each requirement or manually edit individual questions.
            </p>
          </div>

          <span className="checklist-status-badge">Pending approval</span>
        </div>

        <div className="checklist-metadata-grid">
          <div>
            <span>Generated</span>
            <strong>{formatDate(metadata.created_at)}</strong>
          </div>

          <div className="checklist-review-progress">
            <span>Requirements</span>

            <strong className="checklist-requirement-total">
              {draft.requirements.length}
            </strong>

            <ul
              className="checklist-review-counts"
              aria-label="Review progress"
            >
              <li className="checklist-review-count checklist-review-count--accepted">
                <span>Accepted</span>
                <strong>{acceptedRequirements}</strong>
              </li>

              <li className="checklist-review-count checklist-review-count--regeneration">
                <span>Needs changes</span>
                <strong>{editRequirements}</strong>
              </li>

              <li className="checklist-review-count checklist-review-count--pending">
                <span>Not reviewed</span>
                <strong>{pendingRequirements}</strong>
              </li>
            </ul>
          </div>

          <div>
            <span>Last saved</span>
            <strong>
              {savedAt
                ? savedAt.toLocaleTimeString('en-GB')
                : 'Not saved in this session'}
            </strong>
          </div>
        </div>
      </div>

      <div className="checklist-editor-list">
        {draft.requirements.map((requirement, requirementIndex) => {
          const requirementId = requirement.requirement_id;
          const requirementError = emptyRequirementIds.has(requirementId);

          const review = reviews[requirement.requirement_id];
          const reviewStatus = review?.status;
          const requirementLocked = isRequirementLocked(requirement);
          const questionsEditable =
            !requirementLocked && reviewStatus !== 'accepted';
          const isCollapsed = collapsedRequirementIds.has(
            requirement.requirement_id,
          );

          const cardClassName = [
            'card',
            'checklist-card',
            'checklist-requirement-card',
            requirementError && 'checklist-requirement-card--error',
            reviewStatus === 'accepted' &&
              'checklist-requirement-card--accepted',
            reviewStatus === 'approved' &&
              'checklist-requirement-card--accepted',
            reviewStatus === 'edit' && 'checklist-requirement-card--edit',
            isCollapsed && 'checklist-requirement-card--collapsed',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <section className={cardClassName} key={requirement.requirement_id}>
              <div className="checklist-requirement-heading">
                <div className="checklist-requirement-title">
                  <h3>{requirement.title}</h3>

                  <span className="checklist-question-count">
                    {requirement.checklist.length} question
                    {requirement.checklist.length === 1 ? '' : 's'}
                  </span>
                </div>

                {requirementLocked ? (
                  <div className="review-status review-status--approved">
                    <span>
                      This requirement has already been approved and cannot be
                      modified.
                    </span>
                  </div>
                ) : (
                  <div
                    className="review-controls"
                    role="radiogroup"
                    aria-label={`Review status for ${requirement.title}`}
                  >
                    <label
                      className={`review-option review-option--accepted ${
                        reviewStatus === 'accepted'
                          ? 'review-option--selected'
                          : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`review-${requirement.requirement_id}`}
                        checked={reviewStatus === 'accepted'}
                        disabled={saving}
                        onChange={() =>
                          updateRequirementStatus(
                            requirement.requirement_id,
                            'accepted',
                          )
                        }
                      />

                      <span>Accept</span>
                    </label>

                    <label
                      className={`review-option review-option--edit ${
                        reviewStatus === 'edit' ? 'review-option--selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`review-${requirement.requirement_id}`}
                        checked={reviewStatus === 'edit'}
                        disabled={saving}
                        onChange={() =>
                          updateRequirementStatus(
                            requirement.requirement_id,
                            'edit',
                          )
                        }
                      />

                      <span>Edit</span>
                    </label>
                  </div>
                )}

                <button
                  type="button"
                  className="checklist-collapse-button"
                  aria-expanded={!isCollapsed}
                  aria-label={
                    isCollapsed
                      ? `Expand ${requirement.title}`
                      : `Collapse ${requirement.title}`
                  }
                  onClick={() =>
                    toggleRequirementCollapsed(requirement.requirement_id)
                  }
                >
                  <svg
                    className={
                      isCollapsed
                        ? 'checklist-collapse-icon checklist-collapse-icon--collapsed'
                        : 'checklist-collapse-icon'
                    }
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 7.5 10 12.5 15 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {!isCollapsed && (
                <div className="checklist-requirement-body">
                  {requirement.description && (
                    <p className="checklist-requirement-description">
                      {formatDescription(requirement.description)}
                    </p>
                  )}

                  {/*
                  {reviewStatus === 'edit' && (
                    <div className="review-feedback-panel">
                      <label
                        htmlFor={`feedback-${requirement.requirement_id}`}
                      >
                        Feedback for regeneration
                      </label>

                      <textarea
                        id={`feedback-${requirement.requirement_id}`}
                        className="checklist-form-control review-feedback-input"
                        rows={3}
                        disabled={saving || requirementLocked}
                        placeholder="Explain what should be changed when regenerating this checklist."
                        value={review?.comment ?? ''}
                        onChange={event =>
                          updateRequirementComment(
                            requirement.requirement_id,
                            event.target.value,
                          )
                        }
                      />

                      <button
                        type="button"
                        className="action-button regeneration-button"
                        disabled={
                          saving ||
                          requirementLocked ||
                          !(review?.comment ?? '').trim()
                        }
                        onClick={() => {
                          // TODO: Call regeneration API
                        }}
                      >
                        Regenerate requirement
                      </button>
                    </div>
                  )}
                  */}

                  {requirementError && (
                    <p className="checklist-field-error" role="alert">
                      At least one question is required.
                    </p>
                  )}

                  <h4 className="checklist-questions-heading">Questions</h4>

                  <div className="checklist-question-list">
                    {requirement.checklist.map((item, questionIndex) => {
                      const currentQuestionKey = questionKey(
                        requirement.requirement_id,
                        questionIndex,
                      );
                      const isEditing =
                        editingQuestions.has(currentQuestionKey);
                      const overLimit =
                        item.question.length > MAX_QUESTION_LENGTH;

                      return (
                        <div
                          className="checklist-question-row"
                          key={currentQuestionKey}
                        >
                          <span className="checklist-question-number">
                            {questionIndex + 1}.
                          </span>

                          <div className="checklist-question-content">
                            {!canApprove && (
                              <p className="checklist-question-display">
                                {item.question}
                              </p>
                            )}
                            {canApprove && isEditing ? (
                              <>
                                <textarea
                                  className={
                                    overLimit
                                      ? 'checklist-form-control checklist-form-control--error'
                                      : 'checklist-form-control'
                                  }
                                  rows={1}
                                  value={item.question}
                                  disabled={saving || requirementLocked}
                                  onChange={event =>
                                    updateQuestion(
                                      requirementIndex,
                                      questionIndex,
                                      event.target.value,
                                    )
                                  }
                                  onBlur={() =>
                                    setEditingQuestions(previous => {
                                      const next = new Set(previous);
                                      next.delete(currentQuestionKey);
                                      return next;
                                    })
                                  }
                                />

                                <small
                                  className={
                                    overLimit
                                      ? 'checklist-character-count checklist-character-count--error'
                                      : 'checklist-character-count'
                                  }
                                >
                                  {item.question.length}/{MAX_QUESTION_LENGTH}
                                </small>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className={
                                    overLimit
                                      ? 'checklist-question-display checklist-question-display--error'
                                      : 'checklist-question-display'
                                  }
                                  disabled={
                                    saving ||
                                    requirementLocked ||
                                    !questionsEditable
                                  }
                                  title="Click to edit this question"
                                  onClick={() => {
                                    if (reviewStatus === 'pending') {
                                      updateRequirementStatus(
                                        requirement.requirement_id,
                                        'edit',
                                      );
                                    }

                                    setEditingQuestions(previous => {
                                      const next = new Set(previous);
                                      next.add(currentQuestionKey);
                                      return next;
                                    });
                                  }}
                                >
                                  {item.question || 'Click to enter a question'}
                                </button>

                                {overLimit && (
                                  <small className="checklist-character-count checklist-character-count--error">
                                    {item.question.length}/{MAX_QUESTION_LENGTH}
                                  </small>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {actionError && !approvalDialogOpen && (
        <div className="checklist-error checklist-action-error" role="alert">
          {actionError}
        </div>
      )}

      <div className="checklist-sticky-actions">
        <div>
          {canApprove && dirty ? (
            <span className="checklist-unsaved-indicator">Unsaved changes</span>
          ) : (
            <span className="checklist-saved-indicator">All changes saved</span>
          )}
        </div>

        <div
          className={`checklist-action-group ${
            saving ? 'checklist-action-group--saving' : ''
          }`}
        >
          <button
            type="button"
            className="action-button secondary-button"
            disabled={saving || approving}
            onClick={leavePage}
          >
            Leave page
          </button>

          {canApprove && (
            <>
              <button
                type="button"
                className={`action-button secondary-button checklist-save-button checklist-operation-button ${
                  saving ? 'checklist-operation-button--busy' : ''
                }`}
                disabled={saving || approving || !dirty || hasOverlongQuestion}
                aria-busy={saving}
                onClick={() => void saveChecklist()}
              >
                {hasEditRequests ? 'Save review' : 'Save changes'}
              </button>

              <button
                type="button"
                className="action-button checklist-operation-button"
                disabled={
                  saving ||
                  approving ||
                  hasOverlongQuestion ||
                  hasEditRequests ||
                  pendingRequirements > 0
                }
                onClick={() => void openApprovalDialog()}
              >
                Approve and publish
              </button>
            </>
          )}
        </div>
      </div>

      {canApprove && approvalDialogOpen && approvalChecklist && (
        <ChecklistApprovalDialog
          checklist={approvalChecklist}
          metadata={metadata}
          approving={approving}
          error={actionError}
          onCancel={() => {
            if (!approving) {
              setApprovalDialogOpen(false);
              setApprovalChecklist(null);
              setActionError(null);
            }
          }}
          onApprove={() => void approveChecklist()}
        />
      )}
    </UnsavedChangesGuard>
  );
}
